import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { appIdentityIcon, appNavigation } from '@/app/navigation';
import { cn } from '@/lib/utils';
import { useSessionStore } from '@/state/session-store';
import { useUiStore } from '@/state/ui-store';

export function SidebarNav() {
  const activeMembership = useSessionStore((state) =>
    state.memberships.find((membership) => membership.organization_id === state.activeOrganizationId),
  );
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUiStore(
    useShallow((state) => ({
      sidebarCollapsed: state.sidebarCollapsed,
      toggleSidebarCollapsed: state.toggleSidebarCollapsed,
    })),
  );
  const IdentityIcon = appIdentityIcon;

  return (
    <aside
      className={cn(
        'hidden min-h-screen border-r border-white/10 bg-ink-950/90 px-4 py-5 transition-[width] duration-200 lg:flex lg:flex-col',
        sidebarCollapsed ? 'w-24' : 'w-72',
      )}
    >
      <div className="mb-8">
        <div className={cn('flex items-start justify-between gap-3', sidebarCollapsed && 'justify-center')}>
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-accent-400/25 bg-accent-500/10">
            <IdentityIcon className="h-5 w-5 text-accent-300" />
          </div>
          <button
            type="button"
            className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
            onClick={toggleSidebarCollapsed}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {!sidebarCollapsed ? (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-[0.26em] text-accent-300">NordicCompanyHub</p>
            <h1 className="mt-2 text-2xl font-semibold text-white">Executive operating system</h1>
            <p className="mt-2 text-sm text-slate-400">
              Cross-functional company intelligence for leadership, finance, and legal.
            </p>
          </div>
        ) : null}
      </div>

      <nav className="space-y-1">
        {appNavigation.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'group flex items-center rounded-2xl px-3 py-3 text-sm font-medium transition',
                sidebarCollapsed ? 'justify-center' : 'gap-3',
                isActive ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white',
              )
            }
            title={sidebarCollapsed ? item.label : undefined}
          >
            <item.icon className="h-4 w-4" />
            {!sidebarCollapsed ? (
              <div>
                <p>{item.label}</p>
                <p className="mt-0.5 text-xs font-normal text-slate-500 transition group-hover:text-slate-400">{item.description}</p>
              </div>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className={cn('mt-auto rounded-2xl border border-white/10 bg-white/5 p-4', sidebarCollapsed && 'px-3 py-4')}>
        {!sidebarCollapsed ? (
          <>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Active role</p>
            <p className="mt-2 text-lg font-semibold text-white">{activeMembership?.role ?? 'No membership'}</p>
            <p className="mt-2 text-sm text-slate-400">
              Switching organizations updates row-level access, feature flags, and document visibility.
            </p>
          </>
        ) : (
          <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-accent-300">
            {activeMembership?.role ?? 'none'}
          </p>
        )}
      </div>
    </aside>
  );
}
